import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Plus, Edit2, Trash2, ExternalLink, Eye, EyeOff, Upload, X, Heart, Inbox, Check, Sparkles, ShieldOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEMENT_OPTIONS = [
  { value: "homepage", label: "Homepage" },
  { value: "my_corner", label: "Owner's Perch" },
  { value: "cardio_finish", label: "Cardio Finish" },
  { value: "profile_sidebar", label: "Profile Sidebar" },
  { value: "shop", label: "Shop" },
];

const KIND_OPTIONS = [
  { value: "person", label: "🐦 Person", hint: "Mentor, family, beta tester…" },
  { value: "company", label: "🤝 Company", hint: "Gym, brand, podcast…" },
];

const PERSON_CATEGORIES = ["Mentor", "Family", "Friend", "Beta Tester", "Cheerleader", "Other"];
const COMPANY_CATEGORIES = ["Gym", "Equipment", "Podcast", "Brand", "Studio", "Other"];

async function fileToDataUrl(file, maxDim = 800) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const EMPTY_FORM = {
  supporter_id: null,
  name: "",
  kind: "person",
  tagline: "",
  note: "",
  image_url: "",
  link: "",
  category: "Friend",
  placements: ["homepage"],
  active: true,
  sort_order: 999,
};

export default function AdminSupporters() {
  const { user } = useAuth();
  const [supporters, setSupporters] = useState([]);
  const [insights, setInsights] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("all"); // all | person | company
  const fileInputRef = useRef(null);

  const isOwner = user?.role === "owner";

  async function load() {
    setLoading(true);
    try {
      const [{ data: a }, { data: b }, { data: c }] = await Promise.all([
        axios.get(`${API}/admin/supporters`),
        axios.get(`${API}/admin/supporters/insights`),
        axios.get(`${API}/admin/supporter-submissions?status=pending`),
      ]);
      setSupporters(a?.supporters || []);
      setInsights(b?.supporters || []);
      setSubmissions(c?.submissions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load supporters.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isOwner) load();
  }, [isOwner]);

  // Open form pre-filled from a submission (approval flow).
  // Note: we DON'T close the inbox here — the panel stays open so the owner
  // can immediately review the next pending submission after approving one.
  function openApprove(sub) {
    setForm({
      supporter_id: null,
      _submission_id: sub.submission_id, // internal marker — never sent to /admin/supporters
      _submitter_user_id: sub.submitted_by_user_id || null,
      name: sub.name || "",
      kind: sub.kind || "person",
      tagline: sub.tagline || "",
      note: sub.note || "",
      image_url: sub.image_url || "",
      link: sub.link || "",
      category: sub.category || (sub.kind === "company" ? "Brand" : "Friend"),
      placements: ["homepage"], // owner can adjust before publishing
      active: true,
      sort_order: 999,
      trust_submitter: true, // default ON — opt out if you don't want auto-publish later
    });
  }

  async function rejectSubmission(sub) {
    if (!window.confirm(`Reject submission from "${sub.name}"? This cannot be undone.`)) return;
    try {
      await axios.post(`${API}/admin/supporter-submissions/${sub.submission_id}/reject`);
      toast.success("Submission rejected.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed.");
    }
  }

  async function deleteSubmission(sub) {
    if (!window.confirm(`Delete submission from "${sub.name}" permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/supporter-submissions/${sub.submission_id}`);
      toast.success("Submission deleted.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  }

  function openCreate() {
    setForm({ ...EMPTY_FORM });
  }
  function openEdit(s) {
    setForm({
      supporter_id: s.supporter_id,
      name: s.name || "",
      kind: s.kind || "person",
      tagline: s.tagline || "",
      note: s.note || "",
      image_url: s.image_url || "",
      link: s.link || "",
      category: s.category || "Friend",
      placements: s.placements || [],
      active: s.active !== false,
      sort_order: s.sort_order ?? 999,
    });
  }

  function togglePlacement(v) {
    setForm((f) => ({
      ...f,
      placements: f.placements.includes(v)
        ? f.placements.filter((x) => x !== v)
        : [...f.placements, v],
    }));
  }

  async function save() {
    if (!form.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    setSaving(true);
    try {
      const submissionId = form._submission_id;
      const payload = { ...form };
      delete payload._submission_id;
      delete payload._submitter_user_id;
      const isUpdate = !!payload.supporter_id;
      const updateId = payload.supporter_id;
      if (!isUpdate) delete payload.supporter_id;

      if (submissionId) {
        // Approval flow — promotes submission → supporter via dedicated endpoint
        await axios.post(`${API}/admin/supporter-submissions/${submissionId}/approve`, payload);
        toast.success(`Approved & published ${payload.name}.`);
      } else if (isUpdate) {
        // Normal updates ignore trust_submitter (only meaningful on approve)
        delete payload.trust_submitter;
        await axios.put(`${API}/admin/supporters/${updateId}`, payload);
        toast.success(`Updated ${payload.name}.`);
      } else {
        delete payload.trust_submitter;
        await axios.post(`${API}/admin/supporters`, payload);
        toast.success(`Added ${payload.name}.`);
      }
      setForm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(s) {
    if (!window.confirm(`Delete "${s.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/supporters/${s.supporter_id}`);
      toast.success(`Deleted ${s.name}.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  }

  async function toggleActive(s) {
    try {
      await axios.put(`${API}/admin/supporters/${s.supporter_id}`, {
        ...s,
        active: !s.active,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed.");
    }
  }

  async function revokeTrust(s) {
    if (!window.confirm(`Revoke auto-publish trust from "${s.name}"'s submitter? Their future submissions will go back through the inbox.`)) return;
    try {
      await axios.post(`${API}/admin/supporters/${s.supporter_id}/revoke-trust`);
      toast.success(`Trust revoked from ${s.name}'s submitter.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed.");
    }
  }

  async function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed.");
      return;
    }
    if (file.size > 8_000_000) {
      toast.error("File too large (max 8 MB before compression).");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file, 800);
      setForm((f) => ({ ...f, image_url: dataUrl }));
      toast.success("Image uploaded · saved when you submit the form.");
    } catch {
      toast.error("Couldn't process that image.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white pt-24 px-4">
        <div className="max-w-3xl mx-auto py-20 text-center">
          <h1 className="font-display text-3xl mb-3">Sign in required</h1>
          <p className="text-neutral-400">This page is owner-only.</p>
        </div>
      </div>
    );
  }
  if (!isOwner) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white pt-24 px-4">
        <div className="max-w-3xl mx-auto py-20 text-center">
          <h1 className="font-display text-3xl mb-3">Owner Only</h1>
          <p className="text-neutral-400">You need the owner role to manage supporters.</p>
        </div>
      </div>
    );
  }

  const insightMap = Object.fromEntries(insights.map((x) => [x.supporter_id, x]));
  const filtered = supporters.filter((s) => {
    if (tab === "all") return true;
    return s.kind === tab;
  });
  const personCount = supporters.filter((s) => s.kind === "person").length;
  const companyCount = supporters.filter((s) => s.kind === "company").length;
  const categories = form?.kind === "company" ? COMPANY_CATEGORIES : PERSON_CATEGORIES;

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white" data-testid="admin-supporters-page">
      <section className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
              Owner · Admin
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white flex items-center gap-3">
              Friends &amp; <span className="text-[#D4AF37]">Supporters</span>
              <Heart size={24} className="text-[#DC2626]" />
            </h1>
            <p className="text-sm text-neutral-400 mt-2 max-w-xl">
              People and companies who lift you up — no affiliate deal needed.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setInboxOpen((v) => !v)}
              data-testid="admin-supp-inbox-btn"
              className={
                "relative px-4 py-3 text-xs font-bold uppercase tracking-widest border-2 transition-colors flex items-center gap-2 " +
                (inboxOpen
                  ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                  : "border-zinc-700 text-white hover:border-[#D4AF37] hover:text-[#D4AF37]")
              }
            >
              <Inbox size={14} />
              Inbox
              {submissions.length > 0 && (
                <span
                  data-testid="admin-supp-inbox-badge"
                  className="absolute -top-2 -right-2 bg-[#DC2626] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full border border-black"
                >
                  {submissions.length}
                </span>
              )}
            </button>
            <button
              onClick={openCreate}
              data-testid="admin-supporters-add-btn"
              className="bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 px-5 transition-all flex items-center gap-2"
            >
              <Plus size={14} /> Add Supporter
            </button>
          </div>
        </div>

        {/* Admin section tabs */}
        <div className="flex gap-2 mb-6 border-b border-zinc-900">
          <Link
            to="/admin/partners"
            data-testid="admin-tab-partners"
            className="px-4 py-2.5 text-[10px] uppercase tracking-widest font-bold border-b-2 border-transparent text-neutral-500 hover:text-white hover:border-zinc-600 transition-colors"
          >
            Affiliate Partners
          </Link>
          <span
            data-testid="admin-tab-supporters-active"
            className="px-4 py-2.5 text-[10px] uppercase tracking-widest font-bold border-b-2 border-[#D4AF37] text-[#D4AF37]"
          >
            Friends &amp; Supporters
          </span>
        </div>

        {/* Inbox panel — pending self-submissions */}
        {inboxOpen && (
          <div
            data-testid="admin-supp-inbox-panel"
            className="mb-6 border-2 border-[#D4AF37]/40 bg-gradient-to-br from-[#160B22] to-[#0A0A0A]"
          >
            <div className="px-5 py-4 border-b border-zinc-900 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Inbox size={16} className="text-[#D4AF37]" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                    Pending Submissions
                  </div>
                  <div className="text-xs text-neutral-400">
                    {submissions.length === 0
                      ? "Inbox zero — no submissions to review."
                      : `${submissions.length} waiting for review`}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setInboxOpen(false)}
                className="text-neutral-500 hover:text-white"
                aria-label="Close inbox"
              >
                <X size={16} />
              </button>
            </div>
            {submissions.length > 0 && (
              <div className="p-4 space-y-3" data-testid="admin-supp-submissions-list">
                {submissions.map((sub) => {
                  const isPerson = sub.kind === "person";
                  return (
                    <div
                      key={sub.submission_id}
                      data-testid={`admin-supp-submission-${sub.submission_id}`}
                      className="bg-[#0F0F0F] border border-zinc-800 p-3 flex items-start gap-3 flex-wrap"
                    >
                      <div className={
                        "flex-shrink-0 w-14 h-14 bg-black border border-zinc-900 overflow-hidden " +
                        (isPerson ? "rounded-full" : "")
                      }>
                        {sub.image_url ? (
                          <img src={sub.image_url} alt={sub.name} className="w-full h-full object-contain" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center font-display text-lg text-[#D4AF37]">
                            {(sub.name || "?").charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
                            {isPerson ? "🐦" : "🤝"} {sub.category || "—"}
                          </span>
                          {sub.contact_email && (
                            <a
                              href={`mailto:${sub.contact_email}`}
                              className="text-[9px] text-neutral-500 hover:text-[#D4AF37] underline"
                            >
                              {sub.contact_email}
                            </a>
                          )}
                          {sub.link && (
                            <a
                              href={sub.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[9px] text-neutral-500 hover:text-[#D4AF37] underline flex items-center gap-1"
                            >
                              <ExternalLink size={9} /> link
                            </a>
                          )}
                        </div>
                        <div className="font-display text-base text-white leading-tight">{sub.name}</div>
                        {sub.tagline && (
                          <div className="text-xs text-neutral-400 mt-0.5">{sub.tagline}</div>
                        )}
                        {sub.why && (
                          <div className="text-[11px] text-neutral-500 italic mt-1 border-l-2 border-[#D4AF37]/40 pl-2">
                            "{sub.why}"
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <button
                          type="button"
                          onClick={() => openApprove(sub)}
                          data-testid={`admin-supp-approve-${sub.submission_id}`}
                          className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 border-emerald-600 text-emerald-400 hover:bg-emerald-700 hover:text-white transition-colors flex items-center gap-1.5"
                        >
                          <Check size={12} /> Review &amp; Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => rejectSubmission(sub)}
                          data-testid={`admin-supp-reject-${sub.submission_id}`}
                          className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 border-red-900 text-red-400 hover:bg-red-700 hover:text-white transition-colors flex items-center gap-1.5"
                        >
                          <X size={12} /> Reject
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteSubmission(sub)}
                          data-testid={`admin-supp-delete-sub-${sub.submission_id}`}
                          className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border border-zinc-800 text-neutral-500 hover:text-red-400 hover:border-red-900 transition-colors flex items-center gap-1.5"
                        >
                          <Trash2 size={11} /> Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Insights */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <Stat label="Total" value={supporters.length} />
          <Stat label="Active" value={supporters.filter((s) => s.active).length} />
          <Stat
            label="Lifetime Clicks"
            value={insights.reduce((s, x) => s + (x.click_count || 0), 0).toLocaleString()}
          />
          <Stat
            label="Clicks · 7d"
            value={insights.reduce((s, x) => s + (x.clicks_7d || 0), 0).toLocaleString()}
            highlight
          />
        </div>

        {/* Kind filter */}
        <div className="flex gap-2 mb-5">
          <KindBtn active={tab === "all"} onClick={() => setTab("all")} testId="admin-supp-filter-all">
            All · {supporters.length}
          </KindBtn>
          <KindBtn active={tab === "person"} onClick={() => setTab("person")} testId="admin-supp-filter-person">
            🐦 Friends · {personCount}
          </KindBtn>
          <KindBtn active={tab === "company"} onClick={() => setTab("company")} testId="admin-supp-filter-company">
            🤝 Companies · {companyCount}
          </KindBtn>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center text-neutral-500 py-12">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-neutral-500 py-12 border border-zinc-900 bg-[#0F0F0F]">
            {supporters.length === 0
              ? <>No supporters yet. Click <span className="text-[#D4AF37]">Add Supporter</span> to seed your first.</>
              : "No matches for that filter."}
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-supporters-list">
            {filtered.map((s) => {
              const ins = insightMap[s.supporter_id] || {};
              const isPerson = s.kind === "person";
              return (
                <div
                  key={s.supporter_id}
                  data-testid={`admin-supporter-row-${s.supporter_id}`}
                  className={
                    "bg-[#0F0F0F] border-2 p-4 flex items-center gap-4 flex-wrap " +
                    (s.active ? "border-zinc-800" : "border-zinc-900 opacity-60")
                  }
                >
                  <div className={
                    "flex-shrink-0 w-16 h-16 bg-black border border-zinc-900 overflow-hidden " +
                    (isPerson ? "rounded-full" : "")
                  }>
                    {s.image_url ? (
                      <img src={s.image_url} alt={s.name} className="w-full h-full object-contain" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-display text-xl text-[#D4AF37]">
                        {(s.name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
                        {isPerson ? "🐦 " : "🤝 "}{s.category || "—"}
                      </span>
                      {s.auto_published && (
                        <span
                          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-emerald-900/30 border border-emerald-700 text-emerald-400 font-bold flex items-center gap-1"
                          title="Auto-published — submitter was previously trusted"
                        >
                          <Sparkles size={9} /> Auto
                        </span>
                      )}
                      {s.submitted_by_user_id && (
                        <span
                          data-testid={`admin-supporter-trusted-badge-${s.supporter_id}`}
                          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#160B22] border border-[#4C1D95] text-[#D4AF37] font-bold"
                          title="From a user submission — trust flag may be active"
                        >
                          User-submit
                        </span>
                      )}
                      {s.link && (
                        <span className="text-[9px] uppercase tracking-widest text-neutral-500 flex items-center gap-1">
                          <ExternalLink size={9} /> link
                        </span>
                      )}
                    </div>
                    <div className="font-display text-lg text-white leading-tight">{s.name}</div>
                    {s.tagline && (
                      <div className="text-xs text-neutral-400 mt-0.5 line-clamp-1">{s.tagline}</div>
                    )}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(s.placements || []).map((pl) => (
                        <span
                          key={pl}
                          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[#160B22] border border-[#4C1D95] text-neutral-300"
                        >
                          {pl}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Clicks</div>
                    <div className="font-mono tabular-nums text-2xl text-white">
                      {(ins.click_count || 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-[#D4AF37] font-mono tabular-nums">
                      +{ins.clicks_7d || 0} · 7d
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <button
                      onClick={() => toggleActive(s)}
                      data-testid={`admin-supporter-toggle-${s.supporter_id}`}
                      className={
                        "px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 transition-colors flex items-center gap-1.5 " +
                        (s.active
                          ? "border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                          : "border-zinc-700 text-neutral-400 hover:border-white hover:text-white")
                      }
                    >
                      {s.active ? <Eye size={12} /> : <EyeOff size={12} />}
                      {s.active ? "Live" : "Hidden"}
                    </button>
                    <button
                      onClick={() => openEdit(s)}
                      data-testid={`admin-supporter-edit-${s.supporter_id}`}
                      className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 border-zinc-700 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center gap-1.5"
                    >
                      <Edit2 size={12} /> Edit
                    </button>
                    <button
                      onClick={() => remove(s)}
                      data-testid={`admin-supporter-delete-${s.supporter_id}`}
                      className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border-2 border-red-900 text-red-400 hover:bg-red-700 hover:text-white transition-colors flex items-center gap-1.5"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                    {s.submitted_by_user_id && (
                      <button
                        onClick={() => revokeTrust(s)}
                        data-testid={`admin-supporter-revoke-trust-${s.supporter_id}`}
                        title="Revoke auto-publish trust from this user. Their next submission will go back through the inbox."
                        className="px-2.5 py-1.5 text-[10px] uppercase tracking-widest font-bold border border-zinc-800 text-neutral-500 hover:text-amber-400 hover:border-amber-900 transition-colors flex items-center gap-1.5"
                      >
                        <ShieldOff size={11} /> Revoke Trust
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Form modal */}
      {form && (
        <div
          className="fixed inset-0 bg-black/85 z-[70] flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => !saving && setForm(null)}
        >
          <div
            className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-2xl w-full p-6 sm:p-8 my-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-supporters-form"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
              {form._submission_id
                ? "Review · Approve Submission"
                : form.supporter_id ? "Edit Supporter" : "New Supporter"}
            </div>
            <h2 className="font-display text-3xl text-white mb-5">
              {form._submission_id
                ? `Approve ${form.name}`
                : form.supporter_id ? form.name : "Add a friend or supporter"}
            </h2>

            {/* Kind toggle */}
            <Field label="Type">
              <div className="grid grid-cols-2 gap-2" data-testid="form-supp-kind">
                {KIND_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm({
                      ...form,
                      kind: opt.value,
                      category: (opt.value === "company" ? COMPANY_CATEGORIES : PERSON_CATEGORIES)[0],
                    })}
                    data-testid={`form-supp-kind-${opt.value}`}
                    className={
                      "px-3 py-2.5 text-xs font-bold border-2 transition-colors text-left " +
                      (form.kind === opt.value
                        ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                        : "border-zinc-800 text-neutral-400 hover:border-zinc-600")
                    }
                  >
                    <div className="uppercase tracking-widest">{opt.label}</div>
                    <div className="text-[9px] text-neutral-500 normal-case font-normal tracking-normal mt-0.5">
                      {opt.hint}
                    </div>
                  </button>
                ))}
              </div>
            </Field>

            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <Field label={form.kind === "company" ? "Company Name *" : "Name *"}>
                <input
                  data-testid="form-supp-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={form.kind === "company" ? "e.g. Iron Castle Gym" : "e.g. Mom"}
                  className="form-input"
                />
              </Field>
              <Field label="Category">
                <select
                  data-testid="form-supp-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="form-input"
                >
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Tagline (short subtitle on card)">
              <input
                data-testid="form-supp-tagline"
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                placeholder="One-liner about their role/contribution"
                className="form-input"
              />
            </Field>
            <Field label="Personal Note (Roc's shoutout)">
              <textarea
                data-testid="form-supp-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Why this person/company matters to OmniMuscle"
                rows={3}
                className="form-input resize-none"
              />
            </Field>

            {/* Image upload */}
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">
                {form.kind === "company" ? "Logo" : "Photo"}
              </div>
              <div className="flex gap-3 items-start">
                <div className={
                  "flex-shrink-0 w-24 h-24 bg-black border border-zinc-800 overflow-hidden relative " +
                  (form.kind === "person" ? "rounded-full" : "")
                }>
                  {form.image_url ? (
                    <>
                      <img src={form.image_url} alt="preview" className="w-full h-full object-contain" />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, image_url: "" })}
                        data-testid="form-supp-image-clear"
                        title="Clear image"
                        className="absolute top-0 right-0 bg-black/80 text-red-400 hover:text-red-200 p-0.5"
                      >
                        <X size={12} />
                      </button>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] uppercase tracking-widest text-neutral-700">
                      No img
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    data-testid="form-supp-image-file"
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="form-supp-image-upload-btn"
                    className="w-full px-3 py-2 text-[10px] uppercase tracking-widest font-bold border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Upload size={12} />
                    {uploading ? "Compressing…" : "Upload Image"}
                  </button>
                  <input
                    data-testid="form-supp-image-url"
                    value={form.image_url?.startsWith("data:") ? "(uploaded image)" : form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    disabled={form.image_url?.startsWith("data:")}
                    placeholder="…or paste an image URL"
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            <Field label="External Link (optional — IG, website, podcast…)">
              <input
                data-testid="form-supp-link"
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                placeholder="https://…"
                className="form-input"
              />
            </Field>

            <Field label="Placements (where this card appears)">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="form-supp-placements">
                {PLACEMENT_OPTIONS.map((opt) => {
                  const on = form.placements.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => togglePlacement(opt.value)}
                      data-testid={`form-supp-placement-${opt.value}`}
                      className={
                        "px-2.5 py-2 text-[10px] uppercase tracking-widest font-bold border-2 transition-colors " +
                        (on
                          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                          : "border-zinc-800 text-neutral-400 hover:border-zinc-600")
                      }
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </Field>

            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              <Field label="Sort Order (lower = first)">
                <input
                  type="number"
                  data-testid="form-supp-sort-order"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "999", 10) })}
                  className="form-input"
                />
              </Field>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, active: !form.active })}
                  data-testid="form-supp-active"
                  className={
                    "w-full px-3 py-2 text-[10px] uppercase tracking-widest font-bold border-2 transition-colors flex items-center justify-center gap-2 " +
                    (form.active
                      ? "border-emerald-600 bg-emerald-900/20 text-emerald-400"
                      : "border-zinc-800 text-neutral-400 hover:border-zinc-600")
                  }
                >
                  {form.active ? <Eye size={12} /> : <EyeOff size={12} />}
                  {form.active ? "Live" : "Hidden"}
                </button>
              </div>
            </div>

            {/* Trust submitter — only shown during approval flow for signed-in submitters */}
            {form._submission_id && form._submitter_user_id && (
              <div className="mb-5 border-2 border-[#4C1D95]/60 bg-[#160B22]/50 p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="form-supp-trust-submitter"
                    checked={form.trust_submitter !== false}
                    onChange={(e) => setForm({ ...form, trust_submitter: e.target.checked })}
                    className="mt-1 accent-[#D4AF37] cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold flex items-center gap-1.5">
                      <Sparkles size={12} /> Trust this submitter
                    </div>
                    <div className="text-[11px] text-neutral-400 mt-1 leading-relaxed">
                      When checked, this user's future Supporter submissions will auto-publish to the wall instantly — skipping the inbox queue. You can revoke trust anytime from the supporter row.
                    </div>
                  </div>
                </label>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setForm(null)}
                disabled={saving}
                data-testid="form-supp-cancel"
                className="flex-1 border border-zinc-700 hover:border-white text-white font-bold uppercase tracking-widest text-xs py-3 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                data-testid="form-supp-save"
                className="flex-1 bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 transition-all disabled:opacity-50"
              >
                {saving
                  ? "Saving…"
                  : form._submission_id
                    ? "Approve & Publish"
                    : form.supporter_id ? "Update" : "Add Supporter"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .form-input {
          width: 100%;
          background: #0A0A0A;
          border: 1px solid #27272a;
          color: #ffffff;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s;
        }
        .form-input:focus { border-color: #D4AF37; }
        .form-input:disabled { opacity: 0.6; }
      `}</style>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={
      "border p-3 " +
      (highlight ? "border-[#D4AF37]/50 bg-[#160B22]" : "border-zinc-900 bg-[#0F0F0F]")
    }>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={
        "font-mono tabular-nums text-2xl font-bold " +
        (highlight ? "text-[#D4AF37]" : "text-white")
      }>
        {value}
      </div>
    </div>
  );
}

function KindBtn({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        "px-3 py-2 text-[10px] uppercase tracking-widest font-bold border-2 transition-colors " +
        (active
          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
          : "border-zinc-800 text-neutral-400 hover:border-zinc-600 hover:text-white")
      }
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}
